import React, {
  ChangeEvent,
  FC,
  FormEvent,
  ReactNode,
} from 'react';

import Color from 'color'
import { motion } from 'motion/react';
import { styled } from '@mui/material';

import {
  Button,
  Card,
  Container,
  DatePicker,
  Div,
  Heading,
  LicenseInput,
  SubmitButton,
  ToggleButtons,
} from '@domain/components';
import { CaseInfo, ContentStructureFormBlock, FormDataCreation, InputName, TypeOfDamage } from '@domain/interfaces';
import { Colors } from '@domain/theming';
import { Scaling } from '@domain/theming'
import { useWindowSize } from '@domain/hooks';

const cardGradient = 'linear-gradient(342deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 37%, rgba(0,0,0,0.08) 91%)';

const FormCard = styled(motion.create(Card) as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
   background-image: ${cardGradient};
        border: 3px solid ${Colors.WHITE};
        border-radius: 0px;
        flex-grow: 0;
        width: 100%;
        position:relative;
        max-width: ${props => Scaling.scaleUpTo4K(520, props.$vwWidth)}px;
        padding: 0;
        margin: auto;
        align-self: flex-start;
        align-items: flex-end;
        opacity: .85;
        will-change: transform, opacity;
        &.card-form-grey {
          background-image: linear-gradient(
            358deg,
            ${Colors.GREY_LIGHT} -10%,
            ${Color(Colors.GREY_LIGHT).lighten(0.06).hex()} 33%,
            ${Colors.GREY_LIGHT} 55%
            );
            .button {
              border-color: ${Colors.BLACK};
              color: ${Colors.BLACK};
              &:hover {
                background: ${Color(Colors.BLACK).rgb().alpha(0.08).string()}
              }
            }
        }
    
        &.single-card {
          min-height:  ${props => Scaling.scaleUpTo4K(468, props.$vwWidth)}px;
          height: 0; //IE
        }
        + .card {
          margin-top: ${props => Scaling.scaleUpTo4K(60, props.$vwWidth)}px;
          margin-bottom: ${props => Scaling.scaleUpTo4K(468, props.$vwWidth)}px;
        }
        p, h3, input, label {
        }
        @media screen and (min-width:1920px) {
          &.large-width-out {
            max-width: ${props => Scaling.scaleUpTo4K(650, props.$vwWidth)}px;
          }
        }
        @media screen and (max-width: 900px) {
          + .card {
            margin-top: 40px;
            margin-bottom:30px;
          }
        }
        @media screen and (max-width: 420px) {
          &.card.card-form {
            padding: 0;
          }
        }     
      `;

const FormCardContainer = styled(
  motion.create(Container) as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
}
)`
        padding: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px ${props => Scaling.scaleUpTo4K(40, props.$vwWidth)}px;
        will-change: transform, opacity;
        + .card-form__container--response {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        .card-form__button--redirect {
          max-width: 100%;
          text-align: center;
          + .card-form__button--redirect {
            margin-top: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px;
          }
        }
        h4, h5 {
          // color: ${props => props.theme.palette.primary.main};
          color: ${props => props.theme.isCustomThemed ? Colors.GREY_DARKER : props.theme.palette.primary.contrastText};
        }
      `;

const CaseForm = styled(motion.div as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
      display: flex;
      flex-direction: column;
      flex: 1 0 auto;
      max-width: ${props => Scaling.scaleUpTo4K(500, props.$vwWidth)}px;
      padding: 0;
      // justify-content: space-between;
      .card-form & .button--submit {
        margin-top: auto;
      }
      > p,
      > ul,
      > h3 {
        max-width: 100%;
      }
      ul {
        padding-inline-start: 0px;
        padding-inline-end: 0px;
        padding-left: ${props => Scaling.scaleUpTo4K(18, props.$vwWidth)}px;
        padding-bottom: ${props => Scaling.scaleUpTo4K(15, props.$vwWidth)}px;
        font-size: ${props => Scaling.textFontSize(13, props.$vwWidth)}px;
      }
      @media screen and (min-width:1920px) {
        &.large-width {
          max-width: ${props => Scaling.scaleUpTo4K(610, props.$vwWidth)}px;
        }
      }
    `;

const StyleCarddHeadingWithCloseButton = styled(Heading as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
    flex:1 0 auto;
    margin-left: ${props => Scaling.scaleUpTo4K(40, props.$vwWidth)}px;
    @media screen and (max-width:900px) {
      margin-left: 10px;
    }
  `;

const StyledCloseButton = styled(Button as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
    .card-form__header-group & {
      flex: 0 0 auto;
      width: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px;
      height: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px;
      padding: 0;
      margin: 0 0 0 auto;
      border-radius: ${props => Scaling.scaleUpTo4K(10, props.$vwWidth)}px;
      box-shadow: none;
    }
  `;
const formVariants = (vwWidth: number) => ({
  hidden: {
    opacity: 0,
    y: Scaling.scaleUpTo4K(50, vwWidth),
    transition: {
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      opacity: {
        duration: 0.68,
        delay: 0.3,
      },
      y: {
        delay: 0.3,
        type: 'spring',
        stiffness: 40,
      }
    },
  }
})

const formInnerVariants = {
  hidden: {
    opacity: 0,
    y: '20%',
    transition: {
      duration: 0.12,
      y: {
        delay: 0.12,
      },
    },
    transitionEnd: {
      zIndex: 0,
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    zIndex: 1,
    transition: {
      type: 'spring',
      stiffness: 30,
      mass: 0.2,
      opacity: {
        delay: 0.22,
        duration: 0.18,
      },
    },
  },
};

interface Props {
  onChangeCreation: (event: ChangeEvent<HTMLInputElement> | Date | null) => void;
  onBlurLicensePlate: () => void;
  onBlurDamageDate: () => void;
  handleResetCreation: () => void;
  handleSubmitDamageType: (event?: FormEvent<HTMLElement>) => void;
  handleDamageType: (event: React.MouseEvent<HTMLElement>, selectedDamageType: TypeOfDamage) => void;
  shouldShowError: (name: InputName, blurred: boolean) => boolean;
  showError: (name: InputName, blurred: boolean) => ReactNode
  formDataCreation: FormDataCreation;
  caseDetails: CaseInfo;
  dateCreationBlurred: boolean;
  licenseCreationBlurred: boolean;
  errors: Record<string, string> | null;
  damageType: TypeOfDamage;
  content: ContentStructureFormBlock;
  isLoading: boolean
}

const FormSG: FC<Props> = ({ isLoading, onChangeCreation, onBlurLicensePlate, onBlurDamageDate, handleResetCreation, handleSubmitDamageType, handleDamageType, shouldShowError, showError, formDataCreation, caseDetails, licenseCreationBlurred, dateCreationBlurred, errors, damageType, content }) => {

  const contentCardOne = content['card-one']
  const contentCardTwo = content['card-two']

  const typeOfDamageOptions: Array<{ value: TypeOfDamage, label: string, ariaLabel: string }> = [{ value: "Carrosserie", label: "Autoschade", ariaLabel: "car" }, { value: "CarrosserieGlas", label: "Auto en ruitschade", ariaLabel: "both" }, { value: "Glas", label: "Ruitschade", ariaLabel: "glass" }]

  const { vwWidth } = useWindowSize();
  const { button: buttonCardOne, title: titleCardOne } = contentCardOne
  const { button: buttonCardTwo, title: titleCardTwo } = contentCardTwo

  const { licensePlateCreation, damageDateCreation } = formDataCreation;
  return (
    <FormCard
      className="card-form single-card"
      shadow={4}
      variants={formVariants(vwWidth)}
      initial="hidden"
      animate="visible"
      $vwWidth={vwWidth}
    >
      <FormCardContainer
        className="card-form__container--fields"
        flexDirection="column"
        flexGrow="1"
        flexShrink="1"
        width="100%"
        variants={formInnerVariants}
        initial={false}
        animate={!caseDetails.damageType ? 'visible' : 'hidden'}
        $vwWidth={vwWidth}
      >
        <CaseForm
          $vwWidth={vwWidth}
        >
          {titleCardOne && <Heading
            level={4}
            textAlign="center"
            marginBottom={Scaling.scaleUpTo4K(30, vwWidth) + 'px'}
            marginTop={Scaling.scaleUpTo4K(10, vwWidth) + 'px'}
            initial={false}
          >
            {titleCardOne}
          </Heading>}
          <ToggleButtons handleChange={handleDamageType} value={damageType} dynamicFontSize={true} arrayOfButtons={typeOfDamageOptions} />
          {<SubmitButton
            value="Jouw opties"
            disabled={false}
            dynamicFontSize={true}
            onClick={handleSubmitDamageType}
          ><span>{buttonCardOne || 'Volgende'}</span>
          </SubmitButton>}
        </CaseForm>
      </FormCardContainer>
      <FormCardContainer
        className="card-form__container--response"
        flexDirection="column"
        flexGrow="1"
        flexShrink="1"
        width="100%"
        variants={formInnerVariants}
        initial={false}
        animate={caseDetails.damageType.length ? 'visible' : 'hidden'}
        $vwWidth={vwWidth}
      >
        <Container
          className="card-form__header-group"
          flexGrow="0"
          margin="0px"
          alignItems="center"
          justifyContent="center"
        >
          {titleCardTwo && <StyleCarddHeadingWithCloseButton
            level={4}
            textAlign="center"
            $vwWidth={vwWidth}
          >
            {titleCardTwo}
          </StyleCarddHeadingWithCloseButton>}
          <StyledCloseButton
            variant="outline"
            onClick={handleResetCreation}
            $vwWidth={vwWidth}
          >
            X
          </StyledCloseButton>
        </Container>
        <CaseForm
          $vwWidth={vwWidth}
        >
          <LicenseInput
            onChange={onChangeCreation}
            value={licensePlateCreation}
            name="licensePlateCreation"
            type="text"
            label="Kenteken"
            onBlur={onBlurLicensePlate}
            error={shouldShowError('licensePlateCreation', licenseCreationBlurred)}
            dynamicFontSize={true}
          />
          {showError('licensePlateCreation', licenseCreationBlurred)}
          <DatePicker
            value={damageDateCreation || ''}
            onChange={onChangeCreation}
            label="Schadedatum"
            onBlur={onBlurDamageDate}
            error={shouldShowError('damageDateCreation', dateCreationBlurred)}
            dynamicFontSize={true}
          />
          {showError('damageDateCreation', dateCreationBlurred)}
          <SubmitButton
            value="Jouw opties"
            disabled={!!errors}
            dynamicFontSize={true}
            loading={isLoading}
          ><span>{buttonCardTwo || 'Schadedossier aanmaken'}</span>
          </SubmitButton>
        </CaseForm>
      </FormCardContainer>
    </FormCard>
  )
};
export default FormSG
